import React, { useContext, useState } from "react";
import QRCode from "qrcode.react";
import moment from "moment";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { FileCopy, WhatsApp } from "@material-ui/icons";
import PhonelinkRingIcon from "@material-ui/icons/PhonelinkRing";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  qrBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    background: "#fff",
    borderRadius: 8,
  },
  linkField: {
    fontFamily: "monospace",
  },
  expires: {
    marginTop: theme.spacing(1),
  },
  loadingBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 220,
  },
}));

/**
 * Gallera Chat PWA — botão de convite.
 *
 * Gera o link único do cliente (POST /webchat/invite) e oferece três saídas:
 * copiar, QR Code (balcão / atendimento presencial) e envio pelo WhatsApp
 * para o próprio contato.
 *
 * Props:
 *  - contactId (obrigatório)
 *  - contactName  : só para o título do diálogo
 *  - ticketId     : se vier, o envio pelo WhatsApp usa este ticket
 *                   (não cria ticket novo)
 *  - expiresInDays: repassado ao backend (opcional)
 *  - variant      : "icon" (padrão) | "button"
 *  - size, className, color: repassados ao botão
 */
const WebChatInviteButton = ({
  contactId,
  contactName,
  ticketId,
  expiresInDays,
  variant = "icon",
  size = "small",
  className,
  color = "secondary",
  disabled = false,
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [invite, setInvite] = useState(null);
  const [messageBody, setMessageBody] = useState("");

  const buildDefaultMessage = (url) =>
    `${i18n.t("webchatInvite.defaultMessage")}\n\n${url}`;

  const fetchInvite = async () => {
    setLoading(true);
    try {
      const payload = { contactId };
      if (expiresInDays) payload.expiresInDays = expiresInDays;

      const { data } = await api.post("/webchat/invite", payload);
      setInvite(data);
      setMessageBody(buildDefaultMessage(data?.url || ""));
    } catch (err) {
      toastError(err);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!contactId) {
      toast.error(i18n.t("webchatInvite.noContact"));
      return;
    }
    setInvite(null);
    setOpen(true);
    fetchInvite();
  };

  const handleClose = () => {
    setOpen(false);
    setSending(false);
  };

  const handleCopy = () => {
    if (!invite?.url) return;
    navigator.clipboard
      .writeText(invite.url)
      .then(() => {
        toast.success(i18n.t("webchatInvite.toasts.copied"));
      })
      .catch((err) => {
        toastError(err);
      });
  };

  /**
   * Reaproveita o caminho que o painel já usa para falar com um contato:
   * POST /tickets (200 mesmo quando reaproveita ticket aberto do próprio
   * usuário; 403 quando outro atendente está com o contato) e depois
   * POST /messages/:ticketId.
   */
  const resolveTicketId = async () => {
    if (ticketId) return ticketId;

    const { data } = await api.post("/tickets", {
      contactId,
      userId: user?.id,
      status: "open",
    });
    return data?.id;
  };

  const handleSendWhatsApp = async () => {
    if (!invite?.url) return;
    const body = String(messageBody || "").trim();
    if (!body) {
      toast.error(i18n.t("webchatInvite.emptyMessage"));
      return;
    }

    setSending(true);
    try {
      const targetTicketId = await resolveTicketId();
      if (!targetTicketId) {
        toast.error(i18n.t("webchatInvite.toasts.noTicket"));
        return;
      }

      await api.post(`/messages/${targetTicketId}`, {
        read: 1,
        fromMe: true,
        mediaUrl: "",
        body,
      });

      toast.success(i18n.t("webchatInvite.toasts.sent"));
      handleClose();
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toastError(err);
      }
    } finally {
      setSending(false);
    }
  };

  const expiresLabel = invite?.expiresAt
    ? moment(invite.expiresAt).format("DD/MM/YYYY HH:mm")
    : null;

  const trigger =
    variant === "button" ? (
      <Button
        className={className}
        size={size}
        color="primary"
        variant="outlined"
        startIcon={<PhonelinkRingIcon />}
        onClick={handleOpen}
        disabled={disabled}
      >
        {i18n.t("webchatInvite.button")}
      </Button>
    ) : (
      <Tooltip title={i18n.t("webchatInvite.tooltip")}>
        <span>
          <IconButton
            className={className}
            size={size}
            onClick={handleOpen}
            disabled={disabled}
          >
            <PhonelinkRingIcon color={color} />
          </IconButton>
        </span>
      </Tooltip>
    );

  return (
    <>
      {trigger}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {contactName
            ? `${i18n.t("webchatInvite.title")} — ${contactName}`
            : i18n.t("webchatInvite.title")}
        </DialogTitle>
        <DialogContent dividers>
          {loading || !invite ? (
            <div className={classes.loadingBox}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" paragraph>
                {i18n.t("webchatInvite.help")}
              </Typography>

              <TextField
                fullWidth
                value={invite.url || ""}
                variant="outlined"
                size="small"
                label={i18n.t("webchatInvite.linkLabel")}
                InputProps={{
                  readOnly: true,
                  className: classes.linkField,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={i18n.t("webchatInvite.copy")}>
                        <IconButton
                          onClick={handleCopy}
                          edge="end"
                          size="small"
                          aria-label={i18n.t("webchatInvite.copy")}
                        >
                          <FileCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              {expiresLabel && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  component="div"
                  className={classes.expires}
                >
                  {i18n.t("webchatInvite.expiresAt")}: {expiresLabel}
                </Typography>
              )}

              <Box mt={2} className={classes.qrBox}>
                <QRCode
                  value={invite.url || ""}
                  size={200}
                  level="M"
                  includeMargin
                />
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ marginTop: 8 }}
                >
                  {i18n.t("webchatInvite.qrHelp")}
                </Typography>
              </Box>

              <Divider style={{ marginTop: 24, marginBottom: 16 }} />

              <Typography variant="subtitle2" gutterBottom>
                {i18n.t("webchatInvite.sendTitle")}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                size="small"
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            {i18n.t("webchatInvite.buttons.close")}
          </Button>
          <Button
            onClick={handleCopy}
            color="primary"
            variant="outlined"
            disabled={loading || !invite?.url}
            startIcon={<FileCopy />}
          >
            {i18n.t("webchatInvite.copy")}
          </Button>
          <Button
            onClick={handleSendWhatsApp}
            color="primary"
            variant="contained"
            disabled={loading || sending || !invite?.url}
            startIcon={
              sending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <WhatsApp />
              )
            }
          >
            {i18n.t("webchatInvite.buttons.sendWhatsApp")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WebChatInviteButton;
